import { Component, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { JoiEvent } from 'src/app/store/events.era';
import { jsonCopyObject, str2Date } from 'globals/helpers/helpers';
import { performerDisplayTypes } from 'src/app/store/performers.era';
import { OnsiteActivity, OnsiteProgram } from 'globals/store/onsite.data';
import { exhibitorDisplayTypes } from 'globals/store/exhibitors.data';
import { Wrapper } from 'src/app/components/wrapper.component';
import { Asset } from 'globals/store/assets.data';
import { Dictionary } from '@ngrx/entity';
import { JoiResource } from 'globals/store/resources.data';
import { TagPanelData } from 'globals/store/tags.data';
import { programSessionsActivitesMegaTagReselector } from '../../store/reselectors/programs.reselector';
import { TagPanelViewComponent } from '../manage/tag-panel-view.component';
import { IconHolderComponent } from '../logistics/icon-holder.component';
import { ExpandingWrapperComponent } from 'src/app/components/wrappers/expanding-wrapper.component';
import { AssetLinkComponent } from '../assets/asset-link.component';
import { FileLinkComponent } from 'src/app/components/file-handler/file-link.component';
import { LabelTierDialogComponent } from 'src/app/components/label-marker/label-tier.dialog.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { IconComponent } from 'src/app/components/icon/icon.component';
import { DialogTitleComponent } from '../program/dialog-title';
import { CommonModule } from '@angular/common';
import { IdsToItemsPipe } from 'src/app/services/pipes/sortByIds.pipe';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'view-activity-dialog',
    template: `
<div class="view_detail_dialog joi-dialog">
	<icon class="close" aria-label="Close" (click)="dialogRef.close()" matIcon="close"></icon>
	<dialog-title [title]="'Activity Information'" [backgroundColor]="uiService.labelsByType.activity.color" ></dialog-title>
	<div *ngIf="activity" mat-dialog-content>
			<div class="field sm">
					<icon class="tick" *ngIf="activity.completed" icon="tick_12px"></icon>
					<b>{{activity.title}}</b>
			</div>
			<div class="field sm" *ngIf="date">{{activity.time + ', ' + (date | date:'EEEE dd MMMM y')}}</div>
			<div class="field">
				<mat-slide-toggle [disabled]="!currentEvent.can_access" (toggleChange)="updateActivityCompleted()" [(ngModel)]="activity.completed" color="primary">Activity completed</mat-slide-toggle>
			</div>
			<div class="field" *ngIf="activity.description">
					<div class="name">ACTIVITY DESCRIPTION</div>
					<div class="value" [innerHTML]="helpers.strWithBreak(activity.description)"></div>
      </div>
      <div class="field" *ngIf="data.scheduleTitle">
          <div class="name">SCHEDULE</div>
          <div class="field sm">{{ data.scheduleTitle }}</div>
      </div>
      <div class="field" *ngIf="!!location">
          <div class="name">LOCATION</div>
          <div class="field sm">{{ location }}</div>
      </div>
			<div class="group">
					<div class="name">ACTIVITY LABELS</div>
					<div class="not_found" *ngIf="!activity.labels || activity.labels.length==0">No labels</div>
					<label-tier [labels]="activity.labels" [template]="currentEvent.activity_labels"></label-tier>
      </div>

      <div class="group">
					<div class="name">FILES</div>
        <div class="not_found" *ngIf="!activity.files || activity.files.length==0
        ">No files</div>
        <file-link *ngFor="let f of activity.files"
          [file]="f"
          [onlyLink]="true"
        ></file-link>
			</div>

      <div class="group" *ngIf="{ assets: activity?.assetIds | idsToItems:assets } as vars">
        <div class="name">ACTIVITY ASSETS</div>
        <div class="not_found" *ngIf="!vars.assets?.length">No files</div>
        <asset-link *ngFor="let asset of vars.assets"
          (click)="$event.stopPropagation()"
          [asset]="asset"
          [onlyLink]="true"
        ></asset-link>
			</div>

      <expanding-wrapper class="group"
        [title]="'ACTIVITY TAGS'"
        [padded]="true"
        [borderGreyOnCollapse]="true"
      >
        <div class="header-right icon-holder">
          <icon-holder *ngIf="!!tagPanelDatas[activity.id]?.count" [count]="tagPanelDatas[activity.id]?.count"></icon-holder>
        </div>
        <tag-panel-view 
          [tagData]="tagPanelDatas[activity.id]"
          [readOnly]="true"
          [noHeaders]="false"
          [fromLogistics]="true"
          [performers]="currentEvent?.performers"
          [sponsors]="currentEvent?.sponsors"
          [exhibitors]="currentEvent?.exhibitors"
          [resources]="currentEvent?.resources"
          [workforce]="currentEvent?.workforce"
          [attendees]="currentEvent?.attendees"
        ></tag-panel-view>  
      </expanding-wrapper>

	</div>
</div>
`,
    styleUrls: [`onsite.page.scss`],
    standalone: true,
    imports: [TagPanelViewComponent, IconHolderComponent, ExpandingWrapperComponent, AssetLinkComponent, FileLinkComponent, LabelTierDialogComponent, MatSlideToggleModule, IconComponent, DialogTitleComponent, CommonModule, IdsToItemsPipe, FormsModule],
})
export class ViewActivityDialogComponent extends Wrapper<ViewActivityDialogComponent> implements OnDestroy {
	activity: OnsiteActivity
  currentEvent: JoiEvent
  performerDisplayType = performerDisplayTypes.staticCard
  exhibitorDisplayType = exhibitorDisplayTypes.staticCard
  location = ''
  assets: Dictionary<Asset>
  date
  resourceMap: Dictionary<JoiResource> = {}
  tagPanelDatas: Dictionary<TagPanelData> = {}

	constructor(public dialogRef: MatDialogRef<ViewActivityDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: ViewActivityDialogData) {
    super()
    if (typeof data.date === 'string') {
      data.date = str2Date(data.date)
    }
    this.date = data.date

    this.subProp(this.store.select('resources', 'entities'), 'resourceMap')
    this.subProp(this.store.select('events', 'event'), 'currentEvent')
    this.subProp(this.store.select('assets', 'entities'), 'assets')
    this.selectProp(programSessionsActivitesMegaTagReselector, true, 'tagPanelDatas')
    this.subCallback(this.store.select('onsite', 'activities'), activitiesById => {
      this.activity = jsonCopyObject(activitiesById[data.activityId])
      console.log({ activity: this.activity })
    })
    
    if(data.program){
      const { venueId, location } = data.program
      const venueObject = this.currentEvent.venues[venueId]
      if (!venueObject) this.location = location ? location: ''
      else {
        const { venue, address } = venueObject
        this.location = `${location ? location: ''}${location && (venue || address) ? ', ' : ''}${venue ? venue: ''}${venue && address ? ', ' : ''}${address || ''}`
      }      
    }
	}

	updateActivityCompleted() {
		this.activityService.toggleActivityCompleted(this.activity.id)
	}

}

export interface ViewActivityDialogData { // remember, this data can come from either a snapshot or a live program / session object, so need to leave it flexible atm
  program: OnsiteProgram,
  activityId: string,
  date: Date | string,
  scheduleTitle: string
}

